import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormFieldConstants } from "utils/constants";
import { FormHandles } from "models/propTypes/genericFormProps";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { CashReconciliationFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
import { CashReconciliationCreateFormContainerProps } from "models/propTypes/cashReconciliationPropTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { CashReconciliationCreateForm } from "components/cashReconciliation";
import { LoadingWithLogo, useFloatingMessages, useMountedState } from "components/common";
import { RevenueReportingDifferenceFooter, MaxAllowedDifference } from "components/revenueReporting";
import { useCashReconciliationService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CashReconciliationCreateFormContainer: React.ForwardRefRenderFunction<
  FormHandles,
  CashReconciliationCreateFormContainerProps
> = (props, ref) => {
  const cashReconciliationService = useCashReconciliationService();
  const { getMounted } = useMountedState();
  const { enqueueMessage } = useFloatingMessages();
  const [loading, setLoading] = React.useState(false);

  const [footerDifference, setFooterDifference] = React.useState<{ total: number; balanced: boolean }>({
    total: 0,
    balanced: true,
  });

  const innerRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (innerRef?.current ? innerRef.current : ({} as FormHandles)));

  const { dimensions } = useSelector((store: AppState) => {
    return {
      dimensions: store.common.dimensions.cashReconciliationDimensions ?? [],
    };
  });

  const getTotalAmount = (items: CashReconciliationFormTypes.CashReconciliationItem[], isForDifference: boolean) => {
    return (
      items?.reduce(
        (acc, item) =>
          isForDifference
            ? acc +
              (item.accountType === WintSuperkollAPIModel.CashReconciliationItemType.Credit
                ? Number(item.amount ?? 0)
                : -1 * Number(item.amount ?? 0))
            : acc + Number(item.amount ?? 0),
        0
      ) ?? 0
    );
  };

  const onItemAmountChange = (
    name: string,
    value: CashReconciliationFormTypes.CashReconciliationItem[],
    formikProps: FormikProps<CashReconciliationFormTypes.Create>
  ) => {
    const { values, setFieldValue } = formikProps;

    if (name === FormFieldConstants.CashReconciliation.Create.OtherItems) {
      setFieldValue(FormFieldConstants.CashReconciliation.Create.OthersTotalAmount, getTotalAmount(value, false));
    } else if (name === FormFieldConstants.CashReconciliation.Create.PaymentItems) {
      setFieldValue(FormFieldConstants.CashReconciliation.Create.PaymentsTotalAmount, getTotalAmount(value, false));
    } else {
      setFieldValue(FormFieldConstants.CashReconciliation.Create.SalesTotalAmount, getTotalAmount(value, false));
    }

    const salesItems = name === FormFieldConstants.CashReconciliation.Create.SalesItems ? value : values.salesItems ?? [];
    const paymentItems =
      name === FormFieldConstants.CashReconciliation.Create.PaymentItems ? value : values.paymentItems ?? [];
    const otherItems =
      name === FormFieldConstants.CashReconciliation.Create.OtherItems ? value : values.otherItems ?? [];

    const difference =
      getTotalAmount(salesItems, true) + getTotalAmount(paymentItems, true) + getTotalAmount(otherItems, true);

    setFieldValue(FormFieldConstants.CashReconciliation.Create.Total, difference);

    const balanced = Math.abs(difference) <= MaxAllowedDifference;
    setFooterDifference({ total: difference, balanced });

    const hasAmounts = getTotalAmountValidity(salesItems, paymentItems, otherItems);
    props.onFormValidationChange && props.onFormValidationChange(hasAmounts && balanced);
  };

  const setTemplateItems = (template: WintSuperkollAPIModel.CashReconciliationTemplate) => {
    const items = template?.items?.map(
      (item) =>
        ({
          templateItemId: item.id,
          account: item.account,
          accountType: item.accountType,
          description: item.description,
          dimensions: [],
          category: item.category,
        } as CashReconciliationFormTypes.CashReconciliationItem)
    );

    innerRef.current?.setValues &&
      innerRef.current.setValues({
        salesItems:
          items?.filter((i) => i.category === WintSuperkollAPIModel.CashReconciliationItemCategory.Sales) ?? [],
        paymentItems:
          items?.filter((i) => i.category === WintSuperkollAPIModel.CashReconciliationItemCategory.PaymentMethod) ?? [],
        otherItems:
          items?.filter((i) => i.category === WintSuperkollAPIModel.CashReconciliationItemCategory.Other) ?? [],
      });
  };

  React.useEffect(() => {
    setLoading(true);
    cashReconciliationService
      .getTemplate()
      .then((data) => {
        if (getMounted()) {
          setTemplateItems(data);
          setFooterDifference({ total: 0, balanced: true });
          props.onFormValidationChange && props.onFormValidationChange(false);
        }
      })
      .catch(onTemplateValidationError)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const initialValues = React.useMemo(
    () => ({
      filesToUpload: [],
      date: DateHelper.addDays(new Date(), -1),
      salesItems: [],
      paymentItems: [],
      otherItems: [],
      dimensions: [],
      hasRowLevelDimensions: false,
      othersTotalAmount: 0,
      salesTotalAmount: 0,
      paymentsTotalAmount: 0,
    }),
    []
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object<CashReconciliationFormTypes.Create>({
        filesToUpload: Yup.array().of(
          Yup.object<UploadedFile>({
            contentType: Yup.string(),
            fileName: Yup.string(),
            fileSize: Yup.number(),
            data: Yup.string(),
          })
        ),
        date: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .required(TranslationKeys.Validations.Required)
          .max(new Date(), TranslationKeys.Validations.InvalidDate),
        dimensions: Yup.array().of(Yup.string()),
        hasRowLevelDimensions: Yup.boolean(),
        salesItems: Yup.array().of(itemSchema()),
        paymentItems: Yup.array().of(itemSchema()),
        otherItems: Yup.array().of(itemSchema()),
        salesTotalAmount: Yup.number(),
        paymentsTotalAmount: Yup.number(),
        othersTotalAmount: Yup.number(),
        total: Yup.number(),
      }),
    []
  );

  function itemSchema() {
    return Yup.object<CashReconciliationFormTypes.CashReconciliationItem>({
      templateItemId: Yup.number(),
      account: Yup.number(),
      accountType: Yup.mixed().oneOf(Object.values(WintSuperkollAPIModel.CashReconciliationItemType)),
      description: Yup.string(),
      amount: Yup.number(),
      dimensions: Yup.array().of(Yup.string()),
      category: Yup.mixed().oneOf(Object.values(WintSuperkollAPIModel.CashReconciliationItemCategory)),
    });
  }

  const getTotalAmountValidity = (
    salesItems: CashReconciliationFormTypes.CashReconciliationItem[],
    paymentItems: CashReconciliationFormTypes.CashReconciliationItem[],
    otherItems: CashReconciliationFormTypes.CashReconciliationItem[]
  ) => {
    return salesItems
      .concat(paymentItems)
      .concat(otherItems)
      .some((i) => i.amount);
  };

  const getDimensionShortNames = (selectedDimensions: string[]) => {
    let costCenterShortName;
    let projectShortName;

    const projectId = dimensions.find((d) => d.key.compatibilityType === WintSuperkollAPIModel.DimensionTypes.Project)
      ?.key.id;
    const allDimensions = dimensions.flatMap((d) => d.value);

    selectedDimensions.forEach((dim) => {
      const item = allDimensions.find((a) => a.id === dim);

      item?.typeId && item?.typeId === projectId
        ? (projectShortName = item?.shortName)
        : (costCenterShortName = item?.shortName);
    });

    return { costCenter: costCenterShortName, project: projectShortName };
  };

  const getCashReconciliationItems = (items: CashReconciliationFormTypes.CashReconciliationItem[]) => {
    return items.map((i): WintSuperkollAPIModel.CashReconciliationItemInstancePost => {
      const itemDimension = getDimensionShortNames(i.dimensions);
      return {
        templateItemId: i.templateItemId,
        account: i.account,
        amount: Number(i.amount ?? 0),
        costCenter: itemDimension.costCenter,
        project: itemDimension.project,
        category: i.category,
      };
    });
  };

  const handleSubmit = (values: CashReconciliationFormTypes.Create) => {
    const cashReconciliation: WintSuperkollAPIModel.CashReconciliationInstanceDto = {
      attachments: values.filesToUpload?.map((attachment) => ({
        contentType: attachment.contentType,
        data: attachment.data,
        fileName: attachment.fileName,
      })),
      date: DateHelper.resetTime(values.date) ?? new Date(),
      items: getCashReconciliationItems(values.salesItems)
        .concat(getCashReconciliationItems(values.paymentItems))
        .concat(getCashReconciliationItems(values.otherItems)),
    };

    return cashReconciliationService.post(cashReconciliation).catch(onTemplateValidationError);
  };

  const onTemplateValidationError = (errors: any) => {
    const accountErrorCodes = ["AccountInactive", "AccountLocked"];
    if (getMounted() && errors.message) {
      enqueueMessage(
        `${errors.message} ${
          errors.codes && accountErrorCodes.some((e) => errors.codes.includes(e))
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.PleaseContactSupport)
            : ""
        }`,
        StyleEnums.DisplayType.Danger,
        true
      );
    }
    throw errors;
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <CashReconciliationCreateForm
        ref={innerRef}
        {...props}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        itemChangeHandler={onItemAmountChange}
      />
      <RevenueReportingDifferenceFooter total={footerDifference.total} balanced={footerDifference.balanced} show />
    </>
  );
};

export default React.forwardRef(CashReconciliationCreateFormContainer);
